import React, { useState } from 'react';
import { useLanguage } from '@contexts/LanguageContext';
import Modal from '@components/common/Modal';
import Icon from '@components/Icon/Icon';
import Button from '@components/common/Button';
import './AIEditModal.scss';

const AIEditModal = ({
    isOpen,
    onClose,
    onConfirm,
    attachment
}) => {
    const { t } = useLanguage();
    const [prompt, setPrompt] = useState('');
    const [replaceOriginal, setReplaceOriginal] = useState(false);
    const [width, setWidth] = useState(1024);
    const [height, setHeight] = useState(1024);
    const [promptUpsampling, setPromptUpsampling] = useState(false);
    const [safetyTolerance, setSafetyTolerance] = useState(2);
    const [outputFormat, setOutputFormat] = useState('png');
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (prompt.trim() && onConfirm && !isLoading) {
            setIsLoading(true);
            try {
                const params = {
                    prompt: prompt.trim(),
                    replaceOriginal,
                    width,
                    height,
                    promptUpsampling,
                    safetyTolerance,
                    outputFormat
                };

                await onConfirm(params);
            } finally {
                setIsLoading(false);
            }
        }
    };

    const handleConfirmClick = async (e) => {
        e.preventDefault();
        if (prompt.trim() && onConfirm && !isLoading) {
            setIsLoading(true);
            try {
                const params = {
                    prompt: prompt.trim(),
                    replaceOriginal,
                    width,
                    height,
                    promptUpsampling,
                    safetyTolerance,
                    outputFormat
                };

                await onConfirm(params);
            } finally {
                setIsLoading(false);
            }
        }
    };

    const handleClose = () => {
        if (!isLoading) {
            setPrompt('');
            setReplaceOriginal(false);
            setWidth(1024);
            setHeight(1024);
            setPromptUpsampling(false);
            setSafetyTolerance(2);
            setOutputFormat('png');
            setShowAdvanced(false);
            setIsLoading(false);
            onClose();
        }
    };

    // Ensure dimensions are multiples of 32
    const handleDimensionChange = (setter, value) => {
        const numValue = parseInt(value) || 512;
        const rounded = Math.round(numValue / 32) * 32;
        const clamped = Math.max(512, Math.min(2048, rounded));
        setter(clamped);
    };

    if (!attachment) return null;

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title={t('attachments.editWithAI')}
            size="medium"
            closeOnOverlayClick={!isLoading}
        >
            <div className="ai-edit-modal">
                {/* Image preview */}
                <div className="image-preview">
                    <img
                        src={`${process.env.REACT_APP_API_URL}/api/files/serve/${attachment.file.file_path}`}
                        alt={attachment.file.filename}
                        className="preview-image"
                    />
                    <div className="image-info">
                        <span className="filename">{attachment.file.filename}</span>
                    </div>
                </div>

                {/* Edit form */}
                <form onSubmit={handleSubmit} className="edit-form">
                    <div className="form-group">
                        <label htmlFor="edit-prompt" className="form-label">
                            {t('attachments.aiEditPrompt')}
                        </label>
                        <textarea
                            id="edit-prompt"
                            value={prompt}
                            onChange={(e) => setPrompt(e.target.value)}
                            placeholder={t('attachments.aiEditPlaceholder')}
                            className="form-textarea"
                            rows={3}
                            disabled={isLoading}
                            required
                        />
                        <p className="provider-info">
                            <Icon icon="magic" />
                            Powered by Flux Kontext Pro
                        </p>
                    </div>

                    {/* Advanced Options */}
                    {/*
                    <div className="form-group">
                        <Button
                            onClick={() => setShowAdvanced(!showAdvanced)}
                            variant="link"
                            disabled={isLoading}
                            className="advanced-toggle"
                        >
                            <Icon icon={showAdvanced ? "chevron-up" : "chevron-down"} />
                            Advanced Options
                        </Button>
                    </div>
                    */}

                    {showAdvanced && (
                        <div className="advanced-options">
                            <div className="dimensions-group">
                                <div className="form-group">
                                    <label htmlFor="width" className="form-label">
                                        Width (512-2048, multiple of 32)
                                    </label>
                                    <input
                                        id="width"
                                        type="number"
                                        value={width}
                                        onChange={(e) => handleDimensionChange(setWidth, e.target.value)}
                                        min="512"
                                        max="2048"
                                        step="32"
                                        disabled={isLoading}
                                        className="form-input"
                                    />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="height" className="form-label">
                                        Height (512-2048, multiple of 32)
                                    </label>
                                    <input
                                        id="height"
                                        type="number"
                                        value={height}
                                        onChange={(e) => handleDimensionChange(setHeight, e.target.value)}
                                        min="512"
                                        max="2048"
                                        step="32"
                                        disabled={isLoading}
                                        className="form-input"
                                    />
                                </div>
                            </div>

                            <div className="form-group">
                                <label htmlFor="output-format" className="form-label">
                                    Output Format
                                </label>
                                <select
                                    id="output-format"
                                    value={outputFormat}
                                    onChange={(e) => setOutputFormat(e.target.value)}
                                    disabled={isLoading}
                                    className="form-select"
                                >
                                    <option value="jpeg">JPEG</option>
                                    <option value="png">PNG</option>
                                </select>
                            </div>

                            <div className="form-group">
                                <label htmlFor="safety-tolerance" className="form-label">
                                    Safety Tolerance (1=strictest, 6=most lenient)
                                </label>
                                <input
                                    id="safety-tolerance"
                                    type="range"
                                    value={safetyTolerance}
                                    onChange={(e) => setSafetyTolerance(parseInt(e.target.value))}
                                    min="1"
                                    max="6"
                                    disabled={isLoading}
                                    className="form-range"
                                />
                                <span className="range-value">{safetyTolerance}</span>
                            </div>
                        </div>
                    )}


                    <div className="form-group">
                        <label className="checkbox-label">
                            <input
                                type="checkbox"
                                checked={promptUpsampling}
                                onChange={(e) => setPromptUpsampling(e.target.checked)}
                                disabled={isLoading}
                                className="checkbox-input"
                            />
                            <span className="checkbox-text">
                                Enable Prompt Upsampling
                            </span>
                        </label>
                        <p className="option-description">
                            Enhances the prompt for better results
                        </p>
                    </div>

                    <div className="form-group">
                        <label className="checkbox-label">
                            <input
                                type="checkbox"
                                checked={replaceOriginal}
                                onChange={(e) => setReplaceOriginal(e.target.checked)}
                                disabled={isLoading}
                                className="checkbox-input"
                            />
                            <span className="checkbox-text">
                                {t('attachments.replaceOriginal')}
                            </span>
                        </label>
                        <p className="option-description">
                            {replaceOriginal
                                ? t('attachments.replaceOriginal')
                                : t('attachments.createNewCopy')
                            }
                        </p>
                    </div>

                    <div className="form-actions">
                        <Button
                            onClick={handleClose}
                            variant="secondary"
                            disabled={isLoading}
                        >
                            {t('attachments.aiEditCancel')}
                        </Button>
                        <Button
                            onClick={handleConfirmClick}
                            variant="primary"
                            disabled={isLoading || !prompt.trim()}
                        >
                            {isLoading ? (
                                <>
                                    <Icon icon="spinner" spin />
                                    {t('attachments.aiEditInProgress')}
                                </>
                            ) : (
                                <>
                                    <Icon icon="wand-magic-sparkles" />
                                    {t('attachments.aiEditConfirm')}
                                </>
                            )}
                        </Button>
                    </div>
                </form>
            </div>
        </Modal>
    );
};

export default AIEditModal;
